import { useState } from 'react';
import { ArrowLeft, FileText, AlertCircle } from 'lucide-react';

interface PRFIncidentSelectProps {
  onConfirm: (eventNumber: string, incidentNumber: string) => void;
  onBack: () => void;
}

export default function PRFIncidentSelect({ onConfirm, onBack }: PRFIncidentSelectProps) {
  const [eventNumber, setEventNumber] = useState('');
  const [incidentNumber, setIncidentNumber] = useState('');

  const handleSubmit = () => {
    if (eventNumber.trim() && incidentNumber.trim()) {
      onConfirm(eventNumber.trim(), incidentNumber.trim());
    }
  };

  const canSubmit = eventNumber.trim() && incidentNumber.trim();

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-border bg-card">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="p-1.5 rounded-lg hover:bg-accent transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-muted-foreground" />
            </button>
            <div>
              <h2 className="text-lg font-bold text-foreground">Patient Report Form</h2>
              <p className="text-xs text-muted-foreground">Enter incident details</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-2xl mx-auto">
          {/* Info Banner */}
          <div className="mb-6 p-4 rounded-lg bg-blue-500/10 border border-blue-500/20 flex gap-3">
            <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-blue-400 mb-1">No Active Assignment</p>
              <p className="text-xs text-blue-400/80">
                You are not currently assigned to an incident. Please enter the Event Number and Incident Number to create a Patient Report Form.
              </p>
            </div>
          </div>

          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-2xl bg-green-500/10 border-2 border-green-500/20 flex items-center justify-center">
              <FileText className="w-10 h-10 text-green-400" />
            </div>
          </div>

          {/* Form */}
          <div className="space-y-4">
            {/* Event Number */}
            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">
                Event Number <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={eventNumber}
                onChange={(e) => setEventNumber(e.target.value)}
                className="w-full px-4 py-3 rounded-lg bg-background border-2 border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
                placeholder="e.g., EV-1-210426"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Enter the unique event identifier
              </p>
            </div>

            {/* Incident Number */}
            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">
                Incident Number <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={incidentNumber}
                onChange={(e) => setIncidentNumber(e.target.value)}
                className="w-full px-4 py-3 rounded-lg bg-background border-2 border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
                placeholder="e.g., INC-1-210426"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Enter the unique incident identifier
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 border-t border-border bg-card p-4">
        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className={`w-full py-3 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
            canSubmit
              ? 'bg-green-600 hover:bg-green-700 text-white'
              : 'bg-muted text-muted-foreground cursor-not-allowed'
          }`}
        >
          <FileText className="w-4 h-4" />
          Continue to PRF
        </button>
      </div>
    </div>
  );
}
